import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import useGetDoctorRatings from "@base/modules/doctor/hooks/useGetDoctorRatings";
import { DOCTOR_RATING_PAGE_SIZE } from "@base/modules/doctor/doctorProfile";
import { IDoctorRating } from "@base/modules/doctor/interface/doctor.interface";
import RatingCard from "./RatingCard";
import SectionCard from "./SectionCard";

interface IProps {
  doctorCode?: string;
  /** Ratings need a session; without one the block is not requested at all. */
  enabled: boolean;
}

const carouselSettings = {
  dots: true,
  infinite: false,
  speed: 400,
  slidesToShow: 3,
  slidesToScroll: 3,
  arrows: false,
  responsive: [
    { breakpoint: 1200, settings: { slidesToShow: 2, slidesToScroll: 2 } },
    { breakpoint: 640, settings: { slidesToShow: 1, slidesToScroll: 1 } },
  ],
};

/**
 * Patients' ratings of the doctor: a strip of the first page, "See all" for the full paged list, and a
 * card per rating for the whole comment. Hidden when there are none or they could not be loaded.
 */
export default function DoctorRatings({ doctorCode, enabled }: IProps) {
  //! state
  const { t } = useTranslation();
  const { items, totalRecords, isLoading, error, hasMore, loadMore } = useGetDoctorRatings(doctorCode, enabled);
  const { open: openList, shouldRender: shouldRenderList, toggle: toggleList } = useToggle();
  const [selected, setSelected] = useState<IDoctorRating>();
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  //! function
  // The full list loads its next page once its last row scrolls into view.
  useEffect(() => {
    const root = listRef.current;
    const sentinel = sentinelRef.current;

    if (!openList || !root || !sentinel || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && loadMore(), { root });
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [openList, loadMore, items.length]);

  //! render
  if (!enabled || error || !items.length) {
    return null;
  }

  return (
    <SectionCard
      title={t("doctorDetail.ratings")}
      extra={
        <button
          type="button"
          onClick={toggleList}
          className="text-sm lg:text-base font-medium font-['Inter'] text-common-primary bg-transparent border-0 cursor-pointer"
        >
          {t("doctorDetail.seeAll")}
        </button>
      }
    >
      <div className="-mx-2 [&_.slick-dots_li_button::before]:!text-common-primary [&_.slick-track]:flex [&_.slick-slide]:h-auto [&_.slick-slide>div]:h-full [&_.slick-dots]:static [&_.slick-dots]:mt-3 [&_.slick-dots:has(li:only-child)]:hidden">
        <CommonStyles.CarouselUI settings={carouselSettings}>
          {items.slice(0, DOCTOR_RATING_PAGE_SIZE).map((rating, index) => (
            <div key={rating.id ?? index} className="h-full px-2">
              <RatingCard rating={rating} compact onClick={() => setSelected(rating)} />
            </div>
          ))}
        </CommonStyles.CarouselUI>
      </div>

      {shouldRenderList && (
        <CommonStyles.ModalConfirmationUI
          open={openList}
          toggle={toggleList}
          type="normal"
          hiddenAction
          width={640}
          title={t("doctorDetail.ratingsWithCount", { count: totalRecords || items.length })}
          content={
            <div ref={listRef} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {items.map((rating, index) => (
                <RatingCard key={rating.id ?? index} rating={rating} onClick={() => setSelected(rating)} />
              ))}

              {hasMore ? <div ref={sentinelRef} className="h-px flex-none" /> : null}
              {isLoading ? (
                <div className="flex-none flex justify-center py-2">
                  <Spin size="small" />
                </div>
              ) : null}
            </div>
          }
        />
      )}

      {selected ? (
        <CommonStyles.ModalConfirmationUI
          open
          toggle={() => setSelected(undefined)}
          type="normal"
          hiddenAction
          width={560}
          title={t("doctorDetail.ratings")}
          content={<RatingCard rating={selected} />}
        />
      ) : null}
    </SectionCard>
  );
}
